import React, { useMemo, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Sprite, Container } from "@pixi/react";
import { useButtonHover } from "../../../../Hooks/useButtonHover";
import { useTranslation } from "react-i18next";
import { SOUND_NAME } from "../../../../sounds";
import { getUITexture } from "../../../../assets";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { PixiSettings } from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";
import {
  setAutoplaySpeed,
  setPendingAutoplaySpeed,
} from "../../../../State/actions";
import {
  selectAutoPlaySpeed,
  selectPendingAutoPlaySpeed,
  selectIsSpinning,
  selectCriticalAnimationPlaying,
  selectUserInterfaceDisabled,
} from "../../../../State/selectors";

// Speed cycle order
const SPEED_ORDER = ["auto", "quick", "turbo"];

// Texture name for each speed
const SPEED_TEXTURE_MAP = {
  auto: "icon_speed_auto",
  quick: "icon_speed_quick",
  turbo: "icon_speed_turbo",
};

// Minimum gap between clicks (ms) — prevents spam without feeling sluggish
const MIN_CLICK_GAP = 150;

// Same size config as BetSelect
const SPEED_SELECT_BUTTON_CONFIG = {
  BASE_WIDTH: 80,
  BASE_HEIGHT: 80,
  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE_SMALL]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.8,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.45,
      },
    },
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.965,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.6,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 2.3,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.7,
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      deviceMultiplier: 1.0,
    },
  },
};

// Same position config as BetSelect
const SPEED_SELECT_POSITION_CONFIG = {
  [PixiSettings.DEVICE.MOBILE_SMALL]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      x: (toolbar, buttonConfig) =>
        toolbar.width / 2 + buttonConfig.width * 1.7,
      y: (toolbar, buttonConfig, dimensions) => -dimensions.height / 11,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      x: (toolbar, buttonConfig) => toolbar.width - buttonConfig.width * 1.03,
      y: (toolbar, buttonConfig, dimensions) =>
        -dimensions.height / 2 - buttonConfig.height * 0.75,
    },
  },
  [PixiSettings.DEVICE.MOBILE]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      x: (toolbar, buttonConfig) =>
        toolbar.width / 2 + buttonConfig.width * 1.7,
      y: (toolbar, buttonConfig, dimensions) => -dimensions.height / 11,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      x: (toolbar, buttonConfig) => toolbar.width - buttonConfig.width * 1.03,
      y: (toolbar, buttonConfig, dimensions) =>
        -dimensions.height / 2 - buttonConfig.height * 0.75,
    },
  },
  [PixiSettings.DEVICE.TABLET]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      x: (toolbar, buttonConfig) => toolbar.width - buttonConfig.width * 0.95,
      y: (toolbar, buttonConfig, dimensions) =>
        toolbar.height - buttonConfig.height * 2,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      x: (toolbar, buttonConfig) => toolbar.width - buttonConfig.width * 1.05,
      y: (toolbar, buttonConfig) =>
        toolbar.height / 2 - buttonConfig.height * 6.6,
    },
  },
  [PixiSettings.DEVICE.DESKTOP]: {
    x: (toolbar, buttonConfig) => toolbar.width - buttonConfig.width * 0.9,
    y: (toolbar, buttonConfig) => toolbar.height / 2,
  },
};

const ButtonSpeedSelect = ({ toolbar }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const currentSpeed = useSelector(selectAutoPlaySpeed);
  const pendingSpeed = useSelector(selectPendingAutoPlaySpeed);
  const isSpinning = useSelector(selectIsSpinning);
  const criticalAnimationPlaying = useSelector(selectCriticalAnimationPlaying);
  const userInterfaceDisabled = useSelector(selectUserInterfaceDisabled);

  const { getResponsiveConfig, device, orientation, dimensions } =
    useDimensions();

  const {
    hoverContainerRef,
    buttonRef,
    handlePointerOver,
    handlePointerOut,
    triggerPress,
  } = useButtonHover();

  // Simple timestamp throttle — one ref, no timers
  const lastClickRef = useRef(0);

  // The speed to display: pending (user clicked during spin) takes visual priority
  const displaySpeed = pendingSpeed || currentSpeed;

  // Texture for current display speed
  const textureName = SPEED_TEXTURE_MAP[displaySpeed] || SPEED_TEXTURE_MAP.auto;
  const texture = useMemo(() => getUITexture(textureName), [textureName]);

  // Only disable during hard UI locks (not during spins — speed must be changeable)
  const isDisabled = userInterfaceDisabled || criticalAnimationPlaying;

  const buttonConfig = useMemo(
    () => getResponsiveConfig(SPEED_SELECT_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  const buttonPosition = useMemo(() => {
    const deviceConfig = SPEED_SELECT_POSITION_CONFIG[device];
    const posConfig = deviceConfig?.[orientation] ?? deviceConfig;
    return {
      x: posConfig.x(toolbar, buttonConfig, dimensions),
      y: posConfig.y(toolbar, buttonConfig, dimensions),
    };
  }, [device, orientation, toolbar, buttonConfig, dimensions]);

  const handleClick = () => {
    if (isDisabled) return;

    // Throttle: simple timestamp check
    const now = Date.now();
    if (now - lastClickRef.current < MIN_CLICK_GAP) return;
    lastClickRef.current = now;

    soundManager.play(SOUND_NAME.button_click);
    triggerPress();

    // Cycle to next speed based on what's currently displayed
    const idx = SPEED_ORDER.indexOf(displaySpeed);
    const nextSpeed = SPEED_ORDER[(idx + 1) % SPEED_ORDER.length];

    if (isSpinning) {
      // During spin: defer the change to avoid re-renders that corrupt reel state.
      // ReelControlContext / HoldAndWinControlContext already call
      // applyPendingAutoplaySpeed() when their spin completes.
      dispatch(setPendingAutoplaySpeed(nextSpeed));
    } else {
      // Not spinning: apply immediately
      dispatch(setAutoplaySpeed(nextSpeed));
    }
  };

  // Keep sprite texture in sync when displaySpeed changes
  useEffect(() => {
    if (buttonRef.current && texture) {
      buttonRef.current.texture = texture;
    }
  }, [texture, buttonRef]);

  // Manage alpha when disabled state changes
  useEffect(() => {
    if (buttonRef.current) {
      buttonRef.current.alpha = isDisabled ? 0.5 : 1;
    }
  }, [isDisabled, buttonRef]);

  if (!texture) return null;

  return (
    <Container
      name="secondary_button_speed_select"
      x={buttonPosition.x}
      y={buttonPosition.y}
    >
      <Container ref={hoverContainerRef}>
        <Sprite
          ref={buttonRef}
          texture={texture}
          name="button_speed_select_icon"
          anchor={0.5}
          width={buttonConfig.width}
          height={buttonConfig.height}
          x={0}
          y={0}
          eventMode={isDisabled ? "none" : "static"}
          cursor={isDisabled ? "default" : "pointer"}
          alpha={isDisabled ? 0.5 : 1}
          onpointertap={handleClick}
          onpointerover={handlePointerOver}
          onpointerout={handlePointerOut}
          accessible={true}
          accessibleTitle={t("speed", { ns: "common" })}
        />
      </Container>
    </Container>
  );
};

export default ButtonSpeedSelect;
